import { fetchCompanyInfo } from '../../common/fetch.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';

// Scoped to `companyInfo` only for now
export interface SessionData {
  companyInfo?: CompanyInfoResponse;
}

let state: SessionData = {};
const listeners = new Set<() => void>();

const getCompanyInfoContextKey = (mdmId: string | undefined, isEmployee: boolean, isPunchout: boolean): string => {
  if (isEmployee) {
    return 'employee';
  } else if (isPunchout) {
    return 'punchout';
  } else {
    return mdmId ?? '';
  }
};

let loadedForKey: string | undefined;
let pendingKey: string | undefined;
let pendingPromise: Promise<void> | undefined;

// Non-React face — for loaders / non-React code
export const getSessionData = (): SessionData => state;

export const setSessionData = (patch: Partial<SessionData>): void => {
  state = { ...state, ...patch };
  listeners.forEach(listener => listener());
};

export const clearSessionData = (): void => {
  state = {};
  loadedForKey = undefined;
  listeners.forEach(listener => listener());
};

// Subscription glue for useSyncExternalStore (React face)
export const subscribeSessionData = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Single, independent fetch — not routed through Redux/the epic.
export const refreshCompanyInfoSession = (mdmId?: string, isEmployee = false, isPunchout = false): Promise<void> => {
  const key = getCompanyInfoContextKey(mdmId, isEmployee, isPunchout);
  if (loadedForKey === key) {
    return Promise.resolve();
  }
  if (pendingKey === key && pendingPromise) {
    return pendingPromise;
  }
  pendingKey = key;
  pendingPromise = (async () => {
    try {
      const companyInfo = await fetchCompanyInfo(mdmId, isEmployee, isPunchout);
      setSessionData({ companyInfo });
      loadedForKey = key;
    } catch {
      // Swallow — components show or go to error page
      // TODO: Perhaps we should show a notification to user if this fails, this has not been done yet though
    } finally {
      pendingKey = undefined;
      pendingPromise = undefined;
    }
  })();
  return pendingPromise;
};
